import { type FC } from 'react';

import { type KYCReviewReason } from '../../../interface/IAgents';
import NotificationModal from '../modal';

interface ISoftBlockModalProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	reasons: KYCReviewReason[];
	onConfirm: () => void;
	onCancel: () => void;
}

const REASON_COPY: Record<KYCReviewReason, string> = {
	liveness_failed: 'We couldn’t confirm your selfie was a live photo.',
	no_face_detected: 'We couldn’t detect a face in your selfie.',
	low_face_confidence: 'The face match confidence was below 90%.',
	card_not_detected: 'We couldn’t see a voter’s card in the photo you uploaded.',
	invalid_card_document: 'The document didn’t look like a valid voter’s card.',
	low_card_confidence: 'Card detection confidence was below 90%.',
	low_ocr_confidence: 'OCR confidence on the card was below 90%.',
	vin_ocr_failed: 'We couldn’t read the VIN from your card.',
	vin_format_invalid: 'The VIN we read isn’t in the expected format.',
	vin_mismatch: 'The VIN you typed doesn’t match the one on the card.',
	kyc_service_error: 'The verification service didn’t respond in time.',
};

const SoftBlockModal: FC<ISoftBlockModalProps> = ({ isOpen, setIsOpen, reasons, onConfirm, onCancel }) => (
	<NotificationModal
		openModal={isOpen}
		setOpenModal={setIsOpen}
		title='Submit for manual review?'
		closeFunction={onCancel}
	>
		<div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 4px' }}>
			<p
				style={{
					margin: 0,
					fontFamily: 'var(--font-family-regular)',
					fontSize: 'var(--text-body-size)',
					color: 'var(--color-text-secondary)',
					lineHeight: 1.5,
				}}
			>
				A few of your verification checks didn’t pass:
			</p>

			<ul
				style={{
					margin: 0,
					padding: 0,
					listStyle: 'none',
					display: 'flex',
					flexDirection: 'column',
					gap: '10px',
				}}
			>
				{reasons.map((r) => (
					<li
						key={r}
						style={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: '10px',
							padding: '10px 12px',
							borderRadius: '10px',
							backgroundColor: 'var(--color-warning-bg)',
							color: 'var(--color-warning-text)',
							fontFamily: 'var(--font-family-medium)',
							fontSize: '13px',
						}}
					>
						<span aria-hidden>⚠</span>
						<span>{REASON_COPY[r] ?? r}</span>
					</li>
				))}
			</ul>

			<p
				style={{
					margin: 0,
					fontFamily: 'var(--font-family-regular)',
					fontSize: 'var(--text-body-sm-size)',
					color: 'var(--color-text-tertiary)',
					lineHeight: 1.55,
				}}
			>
				If you continue, we’ll submit your application and an admin will review it manually before approval. You can also go back
				and retry the failed checks.
			</p>

			<div
				style={{
					display: 'flex',
					gap: '12px',
					marginTop: '8px',
					justifyContent: 'flex-end',
					flexWrap: 'wrap',
				}}
			>
				<button
					type='button'
					onClick={() => {
						setIsOpen(false);
						onCancel();
					}}
					style={{
						padding: '12px 18px',
						border: '1px solid var(--color-border-strong)',
						borderRadius: '10px',
						backgroundColor: 'transparent',
						color: 'var(--color-text-primary)',
						fontFamily: 'var(--font-family-semibold)',
						fontSize: '14px',
						cursor: 'pointer',
					}}
				>
					Go back and retry
				</button>
				<button
					type='button'
					onClick={() => {
						setIsOpen(false);
						onConfirm();
					}}
					style={{
						padding: '12px 18px',
						border: 'none',
						borderRadius: '10px',
						backgroundColor: 'var(--color-brand-primary)',
						color: '#fff',
						fontFamily: 'var(--font-family-semibold)',
						fontSize: '14px',
						cursor: 'pointer',
					}}
				>
					Submit anyway for review
				</button>
			</div>
		</div>
	</NotificationModal>
);

export default SoftBlockModal;
